import { Layout, message } from 'antd';
import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import Dashboard from './components/Dashboard';
import LandingPage from './components/LandingPage';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import { AuthService } from './services/auth';
import { SocketService } from './services/socket';

const { Header, Content } = Layout;

interface User {
  id: number;
  username: string;
  role: 'worker' | 'admin' | 'controller';
  organizationId?: number;
  organization?: {
    id: number;
    name: string;
    currentQuota: number;
    status: string;
  };
}

function getJwtExp(token: string): number | null {
  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }

  try {
    const payloadBase64Url = parts[1];
    const payloadBase64 = payloadBase64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = payloadBase64.padEnd(payloadBase64.length + ((4 - (payloadBase64.length % 4)) % 4), '=');
    const payload = JSON.parse(atob(padded)) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

function isJwtExpired(token: string, skewSeconds = 30): boolean {
  const exp = getJwtExp(token);
  if (exp === null) {
    return true;
  }
  const nowSeconds = Math.floor(Date.now() / 1000);
  return nowSeconds >= exp - skewSeconds;
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const sessionExpiredHandledRef = useRef(false);
  const sessionExpireTimeoutRef = useRef<number | null>(null);

  const clearSessionExpireTimeout = useCallback(() => {
    if (sessionExpireTimeoutRef.current !== null) {
      window.clearTimeout(sessionExpireTimeoutRef.current);
      sessionExpireTimeoutRef.current = null;
    }
  }, []);

  const clearAuthState = useCallback(() => {
    clearSessionExpireTimeout();
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    AuthService.setToken('');
    SocketService.disconnect();
  }, [clearSessionExpireTimeout]);

  const handleLogout = useCallback(() => {
    sessionExpiredHandledRef.current = false;
    clearAuthState();
    message.success('已退出登录');
  }, [clearAuthState]);

  const handleSessionExpired = useCallback(() => {
    const hasToken = !!localStorage.getItem('token');
    if (!hasToken) {
      clearAuthState();
      return;
    }

    if (sessionExpiredHandledRef.current) {
      clearAuthState();
      return;
    }
    sessionExpiredHandledRef.current = true;
    clearAuthState();
    message.warning('登录已过期，请重新登录');
  }, [clearAuthState]);



  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        AuthService.setToken(token);
        SocketService.connect();
      } catch (error) {
        console.error('Invalid user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const requestInterceptorId = axios.interceptors.request.use(config => {
      const url = config.url;
      const isAuthRequest = typeof url === 'string' && (url.includes('/auth/login') || url.includes('/auth/register'));
      if (isAuthRequest) {
        return config;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        return config;
      }

      const headers = (config.headers || {}) as any;
      if (!headers.Authorization && !headers.authorization) {
        headers.Authorization = `Bearer ${token}`;
        config.headers = headers;
      }

      return config;
    });

    const responseInterceptorId = axios.interceptors.response.use(
      response => response,
      error => {
        const status = error?.response?.status;
        const code = error?.response?.data?.code;
        const url: string | undefined = error?.config?.url;
        const hasToken = !!localStorage.getItem('token');
        const isAuthRequest = typeof url === 'string' && (url.includes('/auth/login') || url.includes('/auth/register'));

        if (hasToken && status === 401 && !isAuthRequest) {
          if (code === 'TOKEN_EXPIRED' || code === 'INVALID_TOKEN' || code === 'NO_TOKEN' || code === 'AUTH_FAILED' || !code) {
            handleSessionExpired();
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptorId);
      axios.interceptors.response.eject(responseInterceptorId);
    };
  }, [handleSessionExpired]);

  useEffect(() => {
    if (!user) {
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    const exp = getJwtExp(token);
    if (exp === null) {
      return;
    }

    const nowMs = Date.now();
    const expireAtMs = (exp - 30) * 1000;
    const delayMs = Math.max(0, expireAtMs - nowMs);
    clearSessionExpireTimeout();
    const timeoutId = window.setTimeout(() => {
      handleSessionExpired();
    }, delayMs);
    sessionExpireTimeoutRef.current = timeoutId;

    return () => {
      window.clearTimeout(timeoutId);
      if (sessionExpireTimeoutRef.current === timeoutId) {
        sessionExpireTimeoutRef.current = null;
      }
    };
  }, [clearSessionExpireTimeout, handleSessionExpired, user]);

  const handleLogin = async (username: string, password: string) => {
    try {
      const response = await AuthService.login(username, password);
      sessionExpiredHandledRef.current = false;
      setUser(response.user);
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      SocketService.connect();
      message.success('登录成功');
      // 登录成功后会由路由自动重定向到 /app
    } catch (error: any) {
      message.error(error.response?.data?.error || '登录失败');
    }
  };

  const handleRegister = async (username: string, password: string, invitationCode: string) => {
    try {
      await AuthService.register(username, password, invitationCode);
      // 注册成功后会由RegisterPage组件处理后续逻辑
    } catch (error: any) {
      throw error; // 让RegisterForm组件处理错误显示
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  // 创建受保护的路由组件
  const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    const notifiedRef = useRef(false);
    const token = localStorage.getItem('token');
    const tokenInvalid = !token || isJwtExpired(token);

    useEffect(() => {
      if (user && tokenInvalid && !notifiedRef.current) {
        notifiedRef.current = true;
        handleSessionExpired();
      }
    }, [handleSessionExpired, tokenInvalid, user]);

    if (loading) {
      return <div>Loading...</div>;
    }
    
    if (!user || tokenInvalid) {
      return <Navigate to="/login" replace state={{ from: location }} />;
    }
    
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Header style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '60px',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          zIndex: 1000,
          padding: '0 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderBottom: '1px solid rgba(0,0,0,0.06)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%' }}>
            <h1 style={{ color: '#333', margin: 0, fontSize: '20px', fontWeight: 'bold' }}>🔮 慧卜</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ color: '#333' }}>
                  {user.username} ({user.role === 'controller' ? '控制者' : user.role === 'admin' ? '管理员' : '工作者'})
                </span>
                {/* 显示组织信息 */}
                {user.organization && (
                  <>
                    <span style={{ fontSize: '14px', color: '#666' }}>
                      组织: {user.organization.name}
                    </span>
                  </>
                )}
              </div>
              <button onClick={handleLogout} style={{ 
                background: 'rgba(255, 255, 255, 0.8)', 
                border: '1px solid #d9d9d9', 
                color: '#333', 
                padding: '6px 16px', 
                cursor: 'pointer',
                borderRadius: '6px',
                fontSize: '14px',
                transition: 'all 0.2s ease'
              }}>
                退出
              </button>
            </div>
          </div>
        </Header>
        <Content style={{ padding: '24px', marginTop: '60px' }}>
          {children}
        </Content>
      </Layout>
    );
  };

  // 创建仅用于未登录用户的路由组件
  const GuestRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    if (loading) {
      return <div>Loading...</div>;
    }
    
    const token = localStorage.getItem('token');
    const tokenValid = !!token && !isJwtExpired(token);

    if (user && tokenValid) {
      return <Navigate to="/app" replace />;
    }
    
    return <>{children}</>;
  };

  return (
    <Router>
      <Routes>
        {/* 营销首页 */}
        <Route path="/" element={<LandingPage />} />
        
        {/* 登录注册页面 - 仅未登录用户可访问 */}
        <Route 
          path="/login" 
          element={
            <GuestRoute>
              <LoginPage onLogin={handleLogin} />
            </GuestRoute>
          } 
        />
        <Route 
          path="/register" 
          element={
            <GuestRoute>
              <RegisterPage onRegister={handleRegister} />
            </GuestRoute>
          } 
        />
        
        {/* 应用主界面 - 需要登录 */}
        <Route 
          path="/app" 
          element={
            <ProtectedRoute>
              <Dashboard user={user!} onLogout={handleLogout} onSessionExpired={handleSessionExpired} />
            </ProtectedRoute>
          } 
        />
        
        {/* 兼容原有的登录注册方式 - 使用原始组件 */}
        <Route 
          path="/old-style" 
          element={
            <Layout style={{ minHeight: '100vh' }}>
              <Header style={{ background: '#001529', color: 'white', padding: '0 24px' }}>
                <h1 style={{ color: 'white', margin: 0 }}>慧卜</h1>
              </Header>
              <Content style={{ padding: '24px' }}>
                {user ? (
                  <Dashboard user={user} onLogout={handleLogout} onSessionExpired={handleSessionExpired} />
                ) : showRegister ? (
                  <RegisterForm
                    onRegister={handleRegister}
                    onBackToLogin={() => setShowRegister(false)}
                  />
                ) : (
                  <LoginForm
                    onLogin={handleLogin}
                    onSwitchToRegister={() => setShowRegister(true)}
                  />
                )}
              </Content>
            </Layout>
          } 
        />
        
        {/* 默认重定向 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
